import type { CareRules, GrowthRules } from '../schemas/data/care.js';

/** Care and contentment (design doc §7, DECISIONS G). Checked by `checkCareRules` in tests. */
export const CARE_RULES: CareRules = {
  maxContentment: 100,
  baselineContentment: 0, // TUNE: neglect only means no bonus (design doc §7)
  startContentment: 50, // TUNE: a new squishy starts half content (owner, 2026-10-03)
  hoursFullToBaseline: 24, // TUNE: design doc §7 [DEFAULT: ~24h from full to baseline]
  fullActionsPerDay: 3, // TUNE: design doc §7 [DEFAULT: 3]
  // TUNE: the 4th action that day gives half, the 5th a quarter, then a little.
  falloffPercents: [50, 25, 10],
  coinsPerFullAction: 1, // TUNE: Patch Coins per full care action (#45)
  dailyCoinCap: 10, // TUNE: design doc §7 [DEFAULT], per account per day
  // TUNE: the soft words for contentment (style guide §2: no numbers in the main UI).
  moods: [
    { id: 'glowing', atLeast: 75, line: 'Glowing with happiness!' },
    { id: 'happy', atLeast: 40, line: 'Happy and bouncy!' },
    { id: 'calm', atLeast: 10, line: 'Calm and content.' },
    { id: 'cuddly', atLeast: 0, line: 'Could use a cuddle!' },
  ],
  // What a feed can be paid with (#621 W1-8b), as a percent of its contentment.
  // Treats are the plain feed; Pies come only from Wild Isle works' Flour,
  // Fish and Salt, so they're worth more.
  foods: {
    treats: 100, // the feed as it's always been
    pies: 150, // TUNE: owner answer Q5 (#621 plan §8)
    // #735 (owner 2026-10-30): Ice's second use. Like Treats for most
    // squishies, like Pies for a Frost one.
    'frosty-treats': { percent: 100, elements: { frost: 150 } }, // TUNE:
  },
};

/** Levels, XP and the care × habitat multiplier (design doc §7–8). Checked by `checkGrowthRules`. */
export const GROWTH_RULES: GrowthRules = {
  maxLevel: 100, // TUNE:
  // TUNE: owner decision 2026-10-06 (design review Q1): keep level 100, but
  // make 30–100 a long tail. Owner decision 2026-11-02 (#730, option 2)
  // quartered both knees' steepness to lift the week-two ceiling.
  // `pnpm sim:progression` (with Training Grounds every night): a casual
  // kid's Partner (4 seats) evolves (16) on day 3–4, is about 23 on day 7,
  // 31 on day 14, 39 on day 30 and 59 on day 60; an engaged kid's (with
  // `battleXpFalloff`) is about 33 / 41 / 54 / 83. Knee 16 is when starters
  // evolve; knee 30 starts the long tail. Pinned by `growth-pace.test.ts`.
  xpCurve: {
    perLevel: 20,
    curve: 20,
    knees: [
      { level: 16, steep: 125 },
      { level: 30, steep: 375 },
    ],
  },
  care: { minPercent: 100, maxPercent: 175 }, // TUNE: design doc §7 [DEFAULT: 1.0× to 1.75×]
  habitat: { onePercent: 135, bothPercent: 175 }, // TUNE: design doc §7 [DEFAULT: up to 1.75×]
  capPercent: 300, // TUNE: design doc §7 [DEFAULT: 3×]
  // TUNE: owner decision 2026-10-06: a befriended squishy joins at most one
  // level below its first evolution, so it grows up by training, not by catch.
  befriendBelowEvolution: 1,
  // TUNE: coordinator call (#382, keeps the line above for kinds that never
  // evolve). Since #443 gave the wave-1 kinds a kid (#493), no shipped species
  // is standalone, so this applies to nothing today; it's kept for future
  // kinds that don't evolve. 18 is the middle first-evolution level among base
  // forms (they run 16 to 30). Lower (15 or 16, one under the earliest evolver) is the strict
  // reading, but then casual Gap-ready slips to day 10 against day 8 with
  // exploring, past the one-day tolerance of #199 (owner decision 2026-10-07 Q6);
  // `pnpm sim:progression` shows it. `care.test.ts` keeps it between the two.
  befriendNoEvolutionLevel: 18,
  // TUNE: owner decision 2026-10-06 (design review, engaged kids): full battle
  // XP for a squishy's first 7 wins of the map-local day, then 10%. A casual
  // kid (about 7 battles a day) never reaches it; `pnpm sim:progression`.
  battleXpFalloff: { fullWinsPerDay: 7, afterPercent: 10 },
};
